"use client"

import Image, { ImageProps } from "next/image"
import { useState, useCallback } from "react"

/**
 * Strips the Cloudflare Image Resizing `/cdn-cgi/image/.../<origin>` prefix
 * and returns the bare origin URL. If the URL doesn't use CF resizing, returns as-is.
 *
 * Example:
 *   https://cdn.example.com/cdn-cgi/image/width=800/https://cdn.example.com/sports/photo.jpg
 *   → https://cdn.example.com/sports/photo.jpg
 */
function stripCfResize(url: string): string | null {
  const marker = "/cdn-cgi/image/"
  const idx = url.indexOf(marker)
  if (idx === -1) return null // Not a CF resizing URL

  // Everything after "/cdn-cgi/image/<options>/" is the origin URL
  const afterMarker = url.substring(idx + marker.length)
  const slashIdx = afterMarker.indexOf("/")
  if (slashIdx === -1) return null

  return afterMarker.substring(slashIdx + 1)
}

/**
 * Drop-in replacement for Next.js `<Image>` that automatically falls back to the
 * direct image URL when Cloudflare Image Resizing fails (e.g. monthly transformation
 * limit exhausted — ERROR 9422).
 *
 * The fallback strips the `/cdn-cgi/image/...` prefix so the browser loads the
 * original (un-resized) image from the Cloudflare Worker proxy cache.
 */
export function ResilientImage({ src, onError, ...props }: ImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src)

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // Only attempt fallback once: if we haven't already swapped
      if (typeof currentSrc === "string") {
        const fallback = stripCfResize(currentSrc)
        if (fallback) {
          setCurrentSrc(fallback)
          return // Don't propagate — we're retrying
        }
      }
      // Propagate to parent onError if no fallback possible
      onError?.(e)
    },
    [currentSrc, onError]
  )

  return <Image {...props} src={currentSrc} onError={handleError} />
}
